import { formatSnakeValue } from 'src/lib/formatters'
import { displayPhoneNumber } from 'src/lib/phone.utils'

import type { ContactAuditField, ContactFieldChange } from './carrier.types'

/**
 * How a recorded change reads on screen.
 *
 * The API reports which field moved and what the values were, as stored — it
 * has no business deciding that a boolean reads "Yes" or that a number is
 * written with brackets. That decision is here, next to the table it is made
 * for, using the same formatters as the rest of these screens.
 */

/** What each field is called in the table. */
export const CONTACT_AUDIT_FIELD_LABEL: Record<ContactAuditField, string> = {
  CONTACT_NAME: 'Name',
  EMAIL: 'Email',
  PHONE_NUMBER: 'Phone',
  ALTERNATIVE_PHONES: 'Other phones',
  STATUS: 'Status',
  PREFERRED: 'Preferred',
  SOURCE: 'Source',
  OCCURRENCE_COUNT: 'Times seen',
  DOT_NUMBER: 'DOT number',
  CARRIER_ID: 'Carrier',
}

export const contactAuditFieldLabel = (field: ContactAuditField) =>
  CONTACT_AUDIT_FIELD_LABEL[field] ?? formatSnakeValue(field)

/**
 * One stored value, in the words this screen uses.
 *
 * Null is left as null rather than turned into a phrase: whether a missing
 * value reads as "nothing" or as an empty cell is the table's call, not this
 * function's.
 */
export const contactAuditValue = (
  field: ContactAuditField,
  value: string | null | undefined
): string | null => {
  if (value === null || value === undefined || value.trim() === '') return null

  switch (field) {
    case 'PHONE_NUMBER':
      return displayPhoneNumber(value)
    case 'ALTERNATIVE_PHONES':
      // Comma-separated on the way in, so each number is formatted on its own
      // and the line is put back together the way this screen writes lists.
      return value
        .split(',')
        .map((entry) => displayPhoneNumber(entry))
        .filter(Boolean)
        .join(', ')
    case 'PREFERRED':
      return value === 'true' ? 'Yes' : 'No'
    case 'STATUS':
    case 'SOURCE':
      return formatSnakeValue(value)
    default:
      return value
  }
}

/** A change as a label and two rendered values. */
export const describeChange = (change: ContactFieldChange) => ({
  label: contactAuditFieldLabel(change.field),
  previousValue: contactAuditValue(change.field, change.previousValue),
  newValue: contactAuditValue(change.field, change.newValue),
})
